/* eslint class-methods-use-this: 0 */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { getToken } from './auth-session-storage.service';
import AgencyApi from './agency-api-resource.service';
import DepartmentApi from './department-api-resource.service';
import EmailApi from './email-api-resource.service';
import HealthApi from './health-api.service';
import InvoiceApi from './invoice-api-resource.service';
import NewsletterApi from './newsletter-api-resource.service';
import OfferApi from './offer-api-resource.service';
import PaymentApi from './payment-api-resource.service';
import ShipmentApi from './shipment-api-resource.service';
import RequestsApi from './request-api-resource.service';
import InternationalCountriesApi from './internationalCountries-api-resource.service';
import StripeApi from './stripe-api-resource.service';
import ColisportBikeApi from './oneway-api-resource.service';
import VatApi from './vat-api-resource.service';
import RevolutApi from './revolut-api-resource.service';
import CreditNoteApi from './creditNote-api-resource.service';
import SpecialOrdersApi from './special-orders-api-resource.service';
import GlsPartnerApi from './gls-partners-api-resource.service';
import SubscriptionApi from './subscription-api-resource.service';
import AccountApi from './account-api-resource.service';
import MailCampaignsApi from './mail-campaign-api-resource.service';
import AdminApi from './admin-api-resource.service';

export default class ColisportApi {
  private static instance: ColisportApi;

  readonly API_SERVER_URL = '/api';

  readonly CONFIG: AxiosRequestConfig = {
    baseURL: this.API_SERVER_URL,
  };

  private readonly httpClient: AxiosInstance = axios.create(this.CONFIG);

  /**
   * Attach the partner / user JWT to every request made through the shared
   * httpClient. This centralizes auth for every resource (bike API, shipment
   * API, admin API, etc.) — without it, calls go out anonymous and any
   * protected route (e.g. the partner-scoped `/bike/shipments` after the
   * R-001 hardening) 401s.
   *
   * Background: `axios.create()` instances do NOT inherit the global
   * `axios.defaults.headers.common.Authorization` that `auth.service.ts`
   * sets on login — so the token has to be re-read on each request from the
   * single source of truth (sessionStorage `authToken`, via `getToken()`).
   */
  // Wired in the constructor so it runs once for the singleton.
  private readonly attachAuthHeader = (): void => {
    this.httpClient.interceptors.request.use((config) => {
      const token = getToken();
      if (token) {
        config.headers = config.headers || {};
        // Don't clobber an explicit Authorization header on a per-call basis.
        if (!('Authorization' in config.headers)) {
          (config.headers as any).Authorization = `Bearer ${token}`;
        }
      }
      return config;
    });
  };

  // API resources

  readonly emailApi: EmailApi;

  readonly offerApi: OfferApi;

  readonly departmentApi: DepartmentApi;

  readonly shipmentApi: ShipmentApi;

  readonly newsletterApi: NewsletterApi;

  readonly paymentApi: PaymentApi;

  readonly invoiceApi: InvoiceApi;

  readonly agencyApi: AgencyApi;

  readonly healthApi: HealthApi;

  readonly requestsApi: RequestsApi;

  readonly internationalCountriesApi: InternationalCountriesApi;

  readonly stripeApi: StripeApi;

  readonly colisportBikeApi: ColisportBikeApi;

  readonly vatApi: VatApi;

  readonly revolutApi: RevolutApi;

  readonly creditNoteApi: CreditNoteApi;

  readonly specialOrdersApi: SpecialOrdersApi;

  readonly glsPartnerApi: GlsPartnerApi;

  readonly subscriptionApi: SubscriptionApi;

  readonly accountApi: AccountApi;

  readonly mailCampaignsApi: MailCampaignsApi;

  readonly adminApi: AdminApi;

  private constructor() {
    this.attachAuthHeader();
    this.shipmentApi = new ShipmentApi(this.httpClient);
    this.offerApi = new OfferApi(this.httpClient);
    this.emailApi = new EmailApi(this.httpClient);
    this.departmentApi = new DepartmentApi(this.httpClient);
    this.newsletterApi = new NewsletterApi(this.httpClient);
    this.paymentApi = new PaymentApi(this.httpClient);
    this.invoiceApi = new InvoiceApi(this.httpClient);
    this.agencyApi = new AgencyApi(this.httpClient);
    this.healthApi = new HealthApi(this.httpClient);
    this.requestsApi = new RequestsApi(this.httpClient);
    this.internationalCountriesApi = new InternationalCountriesApi(this.httpClient);
    this.stripeApi = new StripeApi(this.httpClient);
    this.colisportBikeApi = new ColisportBikeApi(this.httpClient);
    this.vatApi = new VatApi(this.httpClient);
    this.revolutApi = new RevolutApi(this.httpClient);
    this.creditNoteApi = new CreditNoteApi(this.httpClient);
    this.specialOrdersApi = new SpecialOrdersApi(this.httpClient);
    this.glsPartnerApi = new GlsPartnerApi(this.httpClient);
    this.subscriptionApi = new SubscriptionApi(this.httpClient);
    this.accountApi = new AccountApi(this.httpClient);
    this.mailCampaignsApi = new MailCampaignsApi(this.httpClient);
    this.adminApi = new AdminApi(this.httpClient);
  }

  static getInstance(): ColisportApi {
    if (!ColisportApi.instance) {
      ColisportApi.instance = new ColisportApi();
    }
    return ColisportApi.instance;
  }
}
